import { useCreateImprovementProposal } from "@/api/console/improvement-proposals/useCreateImprovementProposal";
import { useSaveImprovementProposal } from "@/api/console/improvement-proposals/useSaveImprovementProposal";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/config/progress";
import toast from "@/lib/toast";
import { ImprovementProposalCreate } from "@/schema/console/improvement-proposals/ImprovementProposalCreate";
import { ImprovementProposal } from "@/schema/improvement-proposal/ImprovementProposal";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";

interface ButtonGroupProps {
  id: string;
}

function ButtonGroup({ id }: ButtonGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const methods = useFormContext<ImprovementProposal>();

  const { trigger: saveImprovementProposal, isMutating: isSaving } =
    useSaveImprovementProposal(id);

  const { trigger: createImprovementProposal, isMutating: isCreating } =
    useCreateImprovementProposal(id, {
      onSuccess: (data) => {
        toast.success(data.message);
        const statues = [
          STATUS.ASSESSMENT_GRADED_COMPLETED,
          STATUS.ASSESSMENT_EVALUATION_COMPLETED,
          STATUS.IMPROVEMENT_PROPOSAL_COMPLETED,
        ];
        mutate(`/console/assessment_progresses?status=${statues.join(",")}`);
        navigate(`/console/check-submissions`);
      },
    });

  const prepareData = (
    data: ImprovementProposal,
  ): ImprovementProposalCreate => {
    const { main_category_results } = data;
    const subCategoryResults = main_category_results.flatMap((mainCategory) => {
      return mainCategory.sub_category_results.map(({ id, proposal_note }) => ({
        id,
        proposal_note: proposal_note ?? "",
      }));
    });
    return {
      sub_category_results: subCategoryResults,
    };
  };

  return (
    <div className=" flex gap-4">
      <Button
        variant="secondary"
        onClick={() =>
          window.open(
            `/console/assessment-results/${id}?isFirstTime=true`,
            "_blank",
          )
        }
      >
        {t("view-report")}
      </Button>
      <Button
        isLoading={isSaving}
        variant="outline"
        onClick={() => {
          methods.handleSubmit((data) => {
            toast.loading(t("save-changes-loading"));
            saveImprovementProposal(prepareData(data));
          })();
        }}
      >
        {t("save-proposal")}
      </Button>

      <Button
        isLoading={isCreating}
        onClick={() => {
          methods.handleSubmit((data) => {
            toast.loading(t("submit-proposal-loading"));
            createImprovementProposal(prepareData(data));
          })();
        }}
      >
        {t("submit-report-and-proposal")}
      </Button>
    </div>
  );
}

export default ButtonGroup;
